import { useMutation, useQuery } from "@tanstack/react-query";
import { useAxios, useApiResponseParser } from "../";

export interface ILaborService {
    Id: string;
    Name: string;
    Unit: string;
    Description?: string | null;
    CalculationFormula?: string | null;
    IsActive?: number;
}

export interface ISupplierLaborInventory {
    Id: string;
    SupplierId?: string; // For creation/update
    LaborServiceId?: string; // For creation/update
    Supplier_Id: string; // From database list
    LaborService_Id: string; // From database list
    UnitPrice: number;
    IsActive?: number;
    SupplierName: string;
    LaborServiceName: string;
    Unit: string;
}

export default function useLabor() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useListLaborServices(isActive: boolean = true) {
        return useQuery({
            queryKey: ["labor-services", isActive],
            queryFn: () =>
                axiosPrivate.get(`/v1/labor-service/list?isActive=${isActive}`).then(parseSuccessResponseData),
        });
    }

    function useListLaborInventory(filters?: { supplierId?: string; laborServiceId?: string }) {
        const queryParams = new URLSearchParams();
        if (filters?.supplierId) queryParams.append("supplierId", filters.supplierId);
        if (filters?.laborServiceId) queryParams.append("laborServiceId", filters.laborServiceId);

        return useQuery({
            queryKey: ["labor-inventory", filters],
            queryFn: () =>
                axiosPrivate.get(`/v1/labor-inventory/list?${queryParams.toString()}`).then(parseSuccessResponseData),
        });
    }

    function useListLaborPriceRanges() {
        return useMutation({
            mutationFn: () => axiosPrivate.get(`/v1/labor-inventory/price-ranges`).then(parseSuccessResponseData),
        });
    }

    // Mutations for Admin panel (if needed later)
    function useCreateLaborService() {
        return useMutation({
            mutationFn: (data: Partial<ILaborService>) =>
                axiosPrivate.post(`/v1/labor-service/create`, data).then(parseSuccessResponseData),
        });
    }

    function useUpdateLaborService() {
        return useMutation({
            mutationFn: (data: Partial<ILaborService>) =>
                axiosPrivate.post(`/v1/labor-service/update`, data).then(parseSuccessResponseData),
        });
    }

    function useCreateLaborInventory() {
        return useMutation({
            mutationFn: (data: Partial<ISupplierLaborInventory>) =>
                axiosPrivate.post(`/v1/labor-inventory/create`, data).then(parseSuccessResponseData),
        });
    }

    function useUpdateLaborInventory() {
        return useMutation({
            mutationFn: (data: Partial<ISupplierLaborInventory>) =>
                axiosPrivate.post(`/v1/labor-inventory/update`, data).then(parseSuccessResponseData),
        });
    }

    function useDeleteLaborInventory() {
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate.delete(`/v1/labor-inventory/delete/${id}`).then(parseSuccessResponseData),
        });
    }

    return {
        useListLaborServices,
        useListLaborInventory,
        useListLaborPriceRanges,
        useCreateLaborService,
        useUpdateLaborService,
        useCreateLaborInventory,
        useUpdateLaborInventory,
        useDeleteLaborInventory,
    };
}
